import axios from '../axiosInstance';
import { getApiBaseUrl, getForwardedHost } from './apiClient';

/**
 * CANsense Silence API Client
 * Handles silence CRUD operations for suppressing alerts
 */

// ============================================================================
// Types
// ============================================================================

export interface SilenceMatcher {
  name: string;
  value: string;
  isRegex: boolean;
}

export type SilenceStatus = 'active' | 'expired';

export interface Silence {
  id: number;
  alertmanagerId: string;
  matchers: SilenceMatcher[];
  startsAt: string;
  endsAt: string;
  createdBy: string;
  comment: string;
  status: SilenceStatus;
  exporterId?: number;
  exporterName?: string;
  createdAt: string;
  updatedAt: string;
}

export interface CreateSilenceRequest {
  exporterId?: number;
  matchers?: SilenceMatcher[];
  durationMinutes: number;
  comment: string;
}

export interface ExtendSilenceRequest {
  durationMinutes: number;
}

export interface SilenceListParams {
  status?: 'active' | 'expired' | 'all';
  exporterId?: number;
}

// ============================================================================
// Helpers
// ============================================================================

const getApiUrl = (endpoint: string = ''): string => {
  return `${getApiBaseUrl()}/sense/silences${endpoint}`;
};

// ============================================================================
// Silence Service
// ============================================================================

export class SilenceService {
  async list(params?: SilenceListParams): Promise<Silence[]> {
    const API_URL = getApiUrl('');
    const queryParams = new URLSearchParams();

    if (params?.status) {
      queryParams.append('status', params.status);
    }
    if (params?.exporterId) {
      queryParams.append('exporter_id', params.exporterId.toString());
    }

    const url = queryParams.toString() ? `${API_URL}?${queryParams}` : API_URL;
    const response = await axios.get(url, {
      headers: {
        'X-Forwarded-Host': getForwardedHost(API_URL),
      },
    });
    const unwrapped = response.data?.data || response.data;
    return unwrapped?.silences || unwrapped;
  }

  async get(id: number): Promise<Silence> {
    const API_URL = getApiUrl(`/${id}`);
    const response = await axios.get(API_URL, {
      headers: {
        'X-Forwarded-Host': getForwardedHost(API_URL),
      },
    });
    return response.data?.data || response.data;
  }

  async create(silenceData: CreateSilenceRequest): Promise<Silence> {
    const API_URL = getApiUrl('');

    if (!silenceData.matchers || silenceData.matchers.length === 0) {
      throw new Error('At least one matcher is required to create a silence');
    }

    const duration = this.formatDuration(silenceData.durationMinutes);
    const apiPayload = {
      matchers: silenceData.matchers,
      duration,
      comment: silenceData.comment,
    };

    const response = await axios.post(API_URL, apiPayload, {
      headers: {
        'X-Forwarded-Host': getForwardedHost(API_URL),
      },
    });
    return response.data?.data || response.data;
  }

  private formatDuration(minutes: number): string {
    if (minutes >= 1440) {
      const days = Math.floor(minutes / 1440);
      const remainingMinutes = minutes % 1440;
      if (remainingMinutes === 0) {
        return `${days}d`;
      }
      const hours = Math.floor(remainingMinutes / 60);
      return hours > 0 ? `${days}d${hours}h` : `${days}d`;
    } else if (minutes >= 60) {
      const hours = Math.floor(minutes / 60);
      const remainingMinutes = minutes % 60;
      if (remainingMinutes === 0) {
        return `${hours}h`;
      }
      return `${hours}h${remainingMinutes}m`;
    } else {
      return `${minutes}m`;
    }
  }

  async delete(id: number): Promise<void> {
    const API_URL = getApiUrl(`/${id}`);
    await axios.delete(API_URL, {
      headers: {
        'X-Forwarded-Host': getForwardedHost(API_URL),
      },
    });
  }

  async quickSilence(
    exporterId: number,
    durationMinutes: number,
    comment: string
  ): Promise<Silence> {
    const API_URL = getApiUrl(`/exporter/${exporterId}`);
    const duration = this.formatDuration(durationMinutes);
    const apiPayload = { duration, comment };
    const response = await axios.post(API_URL, apiPayload, {
      headers: {
        'X-Forwarded-Host': getForwardedHost(API_URL),
      },
    });
    return response.data?.data || response.data;
  }

  async extend(id: number, durationMinutes: number): Promise<Silence> {
    const API_URL = getApiUrl(`/${id}/extend`);
    const duration = this.formatDuration(durationMinutes);
    const apiPayload = { duration };
    const response = await axios.post(API_URL, apiPayload, {
      headers: {
        'X-Forwarded-Host': getForwardedHost(API_URL),
      },
    });
    return response.data?.data || response.data;
  }
}

// Export singleton instance
export const silenceService = new SilenceService();

// Export with short name for convenience
export const silenceApi = silenceService;
